import React, { useState, useEffect, useMemo } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import { Divider, CircularProgress, Box } from '@mui/material';

import { useAuth } from './components/AuthContext';
import { lightTheme, darkTheme } from './styles/theme';

import AuthenticatedLayout from './components/AuthenticatedLayout';
import GuestLayout from './components/GuestLayout';

import LandingPage from './components/landingPage/index';
import Features from './components/landingPage/Features';
import LandingPagePricing from './components/landingPage/Pricing';
import CheckoutForm from './components/CheckoutForm';
import ProfilePage from './components/ProfilePage';
import Dashboard from './components/Dashboard';
import Watchlist from './components/Watchlist';
import Portfolio from './components/Portfolio';
import OptionsCalculator from './components/OptionsCalculator';
import BlackScholesCalculator from './components/BlackScholesCalculator';
import StockReturnCalculator from './components/StockReturnCalculator';
import DailyAggregated from './components/DailyAggregated';
import CryptoAllMarketData from './components/CryptoAllMarketData';
import CoinbaseAllMarketData from './components/CoinbaseAllMarketData';
import BitvavoAllMarketData from './components/BitvavoAllMarketData';
import KrakenAllMarketData from './components/KrakenAllMarketData';
import ByBitAllMarketData from './components/ByBitAllMarketData';
import CryptoArbitrageScanner from './components/CryptoArbitrageScanner';
import AllTopCryptosData from './components/AllTopCryptosData';
import OptionChain from './components/OptionChain';
import UnifiedScreener from './components/UnifiedScreener';
import StockCustomScreener from './components/StockCustomScreener';
import QuotesScreener from './components/QuotesScreener';
import AvgScreener from './components/AvgScreener';
import CryptoAvgScreener from './components/CryptoAvgScreener';
import MarketGainers from './components/MarketGainers';
import MarketLosers from './components/MarketLosers';
import MarketActives from './components/MarketActives';
import Stock from './components/Stock';
import CryptoQuotesScreener from './components/CryptoQuotesScreener';
import CryptoPriceTracker from './components/CryptoPriceTracker';
import SignupContainer from './components/SignupContainer';
import EmailVerification from './components/EmailVerification';
import Login from './components/Login';
import NotFound from './components/NotFound';
import TermsAndConditions from './components/TermsAndConditions';
import PrivacyPolicy from './components/PrivacyPolicy';
import Faq from './components/Faq';
import Footer from './components/Footer';
import Success from './components/Success';
import Cancel from './components/Cancel';
import Pricing from './components/Pricing';
import UpgradePlan from './components/UpgradePlan';
import BlogList from './components/BlogList';
import BlogDetail from './components/BlogDetail';
import StockLists from './components/StockLists';
import MarketCapList from './components/MarketCapList';
import ExchangeList from './components/ExchangeList';
import IndexList from './components/IndexList';
import PennyStockList from './components/PennyStockList';
import CryptoList from './components/CryptoList';
import SectorGroupsList from './components/SectorGroupsList';
import DelistedCompanies from './components/DelistedCompanies';
import EarningsCalendar from './components/EarningsCalendar';
import DividendCalendar from './components/DividendCalendar';
import EconomicCalendar from './components/EconomicCalendar';
import EarningsTranscript from './components/EarningsTranscript';
import AssetComparisonDashboard from './components/AssetComparisonDashboard';
import AnalyticsTracker from './components/AnalyticsTracker';
import CongressTracker from './components/CongressTracker';
import CongressMembers from './components/CongressMembers';
import Politician from './components/Politician';
import InsiderTracker from './components/InsiderTracker';
import ExtendedHoursMovers from './components/ExtendedHoursMovers';
import ViewOnlyPortfolio from './components/ViewOnlyPortfolio';
import MarketMovers from './components/MarketMovers';
import { AlertManagementDashboard } from './components/alerts';
import { AlertCreationDashboard } from './components/alerts';
import ChannelSettings from './components/account/ChannelSettings';
import UnsubscribePage from './components/unsubscribe/UnsubscribePage';
import HedgeFunds from './components/HedgeFunds';
import HedgeFundDetail from './components/HedgeFundDetail';
import News from './components/News';
import POTUSTracker from './components/potus/POTUSTracker';
import RedditTracker from './components/redditTracker/RedditTracker';
import DividendCalculator from './components/DividendCalculator';
import ForgotPassword from './components/ForgotPassword';
import ResetPassword from './components/ResetPassword';
import StockScreener from './components/screener/StockScreener';

// import StockChart from './components/StockChart/StockChart';
// import InteractiveStockChart from './components/InteractiveStockChart';
import Heatmap from './components/Heatmap';
// import Chat from './components/rodeoAi/Chat';
import { RodeoChat } from './components/rodeoAi';

// Initialize Stripe with your publishable key
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

const AppRouter = () => {
  const { isAuthenticated, user, loading } = useAuth();
  const [darkMode, setDarkMode] = useState(() => {
    const savedTheme = localStorage.getItem('darkMode');
    return savedTheme ? JSON.parse(savedTheme) : true;
  });

  useEffect(() => {
    localStorage.setItem('darkMode', JSON.stringify(darkMode));
  }, [darkMode]);

  const theme = useMemo(() => (darkMode ? darkTheme : lightTheme), [darkMode]);

  if (loading) {
    return <div>
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    </div>;
  }

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Router>
        <AnalyticsTracker />
        {isAuthenticated ? (
          <AuthenticatedLayout darkMode={darkMode} setDarkMode={setDarkMode}>
            <Routes>
              <Route path="/" element={<Dashboard />} />
              <Route path="/profile" element={<ProfilePage />} />
              <Route path="/watchlist" element={<Watchlist user={user} />} />
              <Route path="/portfolio" element={<Portfolio user={user} />} />
              <Route path="/lists" element={<StockLists user={user} />} />
              <Route path="/lists/market-cap/:capSize" element={<MarketCapList user={user} />} />
              <Route path="/lists/exchange/:exchangeName" element={<ExchangeList user={user} />} />
              <Route path="/lists/index/:indexName" element={<IndexList user={user} />} />
              <Route path="/lists/penny-stocks" element={<PennyStockList user={user} />} />
              <Route path="/lists/crypto" element={<CryptoList user={user} />} />
              <Route path="/lists/sector/:sectorName" element={<SectorGroupsList user={user} />} />
              <Route path="/lists/delisted" element={<DelistedCompanies user={user} />} />
              <Route path="/options-calculator" element={<OptionsCalculator />} />
              <Route path="/black-scholes-calculator" element={<BlackScholesCalculator />} />
              <Route path="/stock-return-calculator" element={<StockReturnCalculator />} />
              <Route path="/daily-aggregated" element={<DailyAggregated />} />
              <Route path="/crypto-all-market-data" element={<CryptoAllMarketData />} />
              <Route path="/coinbase-all-market-data" element={<CoinbaseAllMarketData />} />
              <Route path="/bitvavo-all-market-data" element={<BitvavoAllMarketData />} />
              <Route path="/kraken-all-market-data" element={<KrakenAllMarketData />} />
              <Route path="/bybit-all-market-data" element={<ByBitAllMarketData />} />
              <Route path="/crypto-arbitrage-scanner" element={<CryptoArbitrageScanner />} />
              <Route path="/all-top-cryptos-data" element={<AllTopCryptosData />} />
              <Route path="/option-chain" element={<OptionChain />} />
              <Route path="/screener_Old" element={<UnifiedScreener />} />
              <Route path="/screener" element={<StockScreener />} />
              <Route path="/stock-custom-screener" element={<StockCustomScreener />} />
              <Route path="/crypto-quotes-screener" element={<CryptoQuotesScreener />} />
              <Route path="/avg-screener" element={<AvgScreener />} />
              <Route path="/crypto-avg-screener" element={<CryptoAvgScreener />} />
              <Route path="/crypto-price-tracker" element={<CryptoPriceTracker />} />
              <Route path="/quotes-screener" element={<QuotesScreener />} />
              <Route path="/market-gainers" element={<MarketGainers />} />
              <Route path="/market-losers" element={<MarketLosers />} />
              <Route path="/market-actives" element={<MarketActives />} />
              <Route path="/market-extended-hours" element={<ExtendedHoursMovers />} />
              <Route path="/asset/:symbol/:tab?/:subtab?" element={<Stock key={window.location.pathname} />} />
              {/*<Route path="/asset/:symbol" element={<Stock key={window.location.pathname} />} />*/}
              {/*  <Route path="/crypto/:symbol" element={<Crypto />} />
              <Route path="/index/:symbol" element={<Index />} /> */}
              <Route path="*" element={<NotFound />} />
              <Route path="/email-verification" element={<EmailVerification />} />
              <Route path="/terms-and-conditions" element={<TermsAndConditions />} />
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              <Route path="/pricing" element={<Pricing />} />
              <Route path="/upgrade" element={<UpgradePlan />} />
              <Route path="/blogs" element={<BlogList />} />
              <Route path="/blogs/:id" element={<BlogDetail />} />
              <Route path="/earnings-calendar" element={<EarningsCalendar />} />
              <Route path="/dividend-calendar" element={<DividendCalendar />} />
              <Route path="/economic-calendar" element={<EconomicCalendar />} />
              <Route path="/earnings-transcript" element={<EarningsTranscript />} />
              <Route path="/congress-tracker" element={<CongressTracker />} />
              <Route path="/congress-members" element={<CongressMembers />} />
              <Route path="/congress-member/:politician" element={<Politician />} />
              <Route path="/insider-tracker" element={<InsiderTracker />} />
              <Route path="/asset-comparison" element={<AssetComparisonDashboard />} />
              <Route path="/portfolio/:nickname/:portfolioName" element={<ViewOnlyPortfolio />} />
              <Route path="/market-movers" element={<MarketMovers />} />
              <Route path="/hedge-funds" element={<HedgeFunds />} />
              <Route path="/hedge-fund/:cik" element={<HedgeFundDetail />} />
              <Route path="/News" element={<News />} />
              <Route path="/potus-tracker" element={<POTUSTracker />} />
              <Route path="/reddit-tracker" element={<RedditTracker />} />
              <Route path="/dividend-calculator" element={<DividendCalculator />} />
              {/*<Route path="/chart/:symbol" element={<StockChart
                symbol="PLTR"
                height={1200}  // Change this! Try 1000, 1200, even 1400
                autoRefresh={false}
              />} />
               <Route path="/InteractiveStockChart/:symbol" element={<InteractiveStockChart symbol="AAPL"/>} /> 
              <Route path="/chat-test" element={<Chat user={user} />} />  */}
              <Route path="/heatmap" element={<Heatmap />} />
              <Route path="/alerts" element={<AlertManagementDashboard />} />
              <Route path="/alerts/new" element={<AlertCreationDashboard />} />
              <Route path="/account/channels" element={<ChannelSettings />} />
              {/* Main chat route - RodeoChat handles everything */}
              <Route path="/chat" element={<RodeoChat user={user} mode="full" />} />
              {/* Chat with specific conversation ID */}
              <Route path="/chat/:conversationId" element={<RodeoChat user={user} />} />
              <Route path="/unsubscribe" element={<UnsubscribePage />} />
              <Route path="/checkout" element={
                <Elements stripe={stripePromise}>
                  <CheckoutForm />
                </Elements>
              } />
              <Route path="/success" element={<Success />} />
              <Route path="/cancel" element={<Cancel />} />
            </Routes>
          </AuthenticatedLayout>
        ) : (
          <GuestLayout darkMode={darkMode} setDarkMode={setDarkMode}>
            <Routes>
              <Route path="/" element={<LandingPage />} />
              <Route path="/Features" element={<Features />} />
              <Route path="/signup" element={<SignupContainer />} />
              <Route path="/email-verification" element={<EmailVerification />} />
              <Route path="/login" element={<Login />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="*" element={<Navigate to="/" replace />} />
              <Route path="/terms-and-conditions" element={<TermsAndConditions />} />
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              <Route path="/faq" element={<Faq />} />
              <Route path="/pricing" element={<LandingPagePricing />} />
              <Route path="/blogs" element={<BlogList />} />
              <Route path="/blogs/:id" element={<BlogDetail />} />
              <Route path="/portfolio/:nickname/:portfolioName" element={<ViewOnlyPortfolio />} />
              <Route path="/checkout" element={
                <Elements stripe={stripePromise}>
                  <CheckoutForm />
                </Elements>
              } />
              <Route path="/unsubscribe" element={<UnsubscribePage />} />
              <Route path="/success" element={<Success />} />
              <Route path="/cancel" element={<Cancel />} />
            </Routes>
          </GuestLayout>
        )}

        <Divider style={{ width: '100%', marginTop: 20 }} />
        <Footer isAuthenticated={isAuthenticated} />
      </Router>
    </ThemeProvider>
  );
};

export default AppRouter;